import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font, Svg, Path, Circle, Rect } from '@react-pdf/renderer';
import logo from '../../components/assets/images/TPOLogoNew.png';

// Основные цвета из дизайна
const COLORS = {
  primaryGreen: '#3CB371', // Основной зеленый (шапка таблицы, иконки)
  lightGreenBg: '#F5FAF7', // Фон для четных строк
  textDark: '#333333',     // Основной текст
  textGray: '#707070',     // Второстепенный текст (SKU, старая цена)
  borderGray: '#EAEAEA',   // Границы таблицы
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    paddingBottom: 60, // Оставляем место для футера
  },
  
  // --- Шапка (Top Bar) ---
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    marginTop: 10,
  },
  logoBlock: {
    flex: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  logoImage: {
    height: 40,
    objectFit: 'contain',
  },
  logoText: {
    color: COLORS.primaryGreen,
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    width: 100,
    lineHeight: 1.2,
  },
  infoBlock: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderLeft: `2 solid ${COLORS.primaryGreen}`,
    paddingLeft: 10,
    height: 35,
    gap: 8,
  },
  infoTextCol: {
    flexDirection: 'column',
    gap: 2,
  },
  infoLabel: {
    color: COLORS.textGray,
    fontFamily: 'Helvetica',
    fontSize: 8,
  },
  infoValue: {
    color: COLORS.textDark,
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
  },

  // --- Таблица (Table) ---
  tableWrapper: {
    borderRadius: 6,
    border: `1 solid ${COLORS.borderGray}`,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: COLORS.primaryGreen,
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  tableHeaderCell: {
    color: '#FFFFFF',
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderTop: `1 solid ${COLORS.borderGray}`,
    backgroundColor: '#FFFFFF',
  },
  rowEven: {
    backgroundColor: COLORS.lightGreenBg,
  },
  
  // Ширина колонок
  colSku: { flex: 1.5 },
  colProduct: { flex: 3.5 },
  colPriceCat: { flex: 1 },
  colPriceComp: { flex: 1, textAlign: 'center' },

  // Стили текста в ячейках
  cellTextSku: {
    color: COLORS.textGray,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  cellTextProduct: {
    color: COLORS.textDark,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  cellTextPriceCat: {
    color: COLORS.textGray,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  cellTextPriceComp: {
    color: COLORS.primaryGreen,
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
  },

  // --- Футер (Footer) ---
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 25,
  },
  footerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  footerText: {
    color: COLORS.textDark,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
});

// --- Вспомогательные функции ---
const formatPrice = (priceStr, ivaStr) => {
  const price = parseFloat(priceStr);
  const iva = parseFloat(ivaStr) || 0;
  if (isNaN(price)) return 'N/A';
  return (price / (1 + iva / 100)).toFixed(2) + '€';
};

// --- Компонент ---
const PDFOrders = ({ contact, items }) => {
  const dateStr = new Date().toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: 'numeric'
  });

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        
        {/* Шапка */}
        <View style={styles.topBar}>
          <View style={styles.logoBlock}>
            <Image source={logo} style={styles.logoImage} />
            <Text style={styles.logoText}>Tu Producto Online</Text>
          </View>
        </View>
        <View style={styles.topBar}>
          <View style={styles.infoBlock}>
            <Svg viewBox="0 0 24 24" width="18" height="18">
              <Path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M3.27 6.96L12 12.01l8.73-5.05" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12 22.08V12" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.infoTextCol}>
              <Text style={styles.infoLabel}>Items count</Text>
              <Text style={styles.infoValue}>{items?.length || 0}</Text>
            </View>
          </View>

          <View style={styles.infoBlock}>
            <Svg viewBox="0 0 24 24" width="18" height="18">
              <Path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Circle cx="12" cy="7" r="4" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.infoTextCol}>
              <Text style={styles.infoLabel}>Cliente</Text>
              <Text style={styles.infoValue}>
                {contact?.name || 'N/A'}{contact?.tradeName ? `\n(${contact?.tradeName})` : ''}
              </Text>
            </View>
          </View>

          <View style={styles.infoBlock}>
            <Svg viewBox="0 0 24 24" width="18" height="18">
              <Rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M16 2v4" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M8 2v4" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M3 10h18" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.infoTextCol}>
              <Text style={styles.infoLabel}>Fecha</Text>
              <Text style={styles.infoValue}>{dateStr}</Text>
            </View>
          </View>
        </View>

        {/* Таблица */}
        <View style={styles.tableWrapper}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colSku]}>SKU</Text>
            <Text style={[styles.tableHeaderCell, styles.colProduct]}>Producto</Text>
            <Text style={[styles.tableHeaderCell, styles.colPriceCat]}>Precio Catálogo</Text>
            <Text style={[styles.tableHeaderCell, styles.colPriceComp]}>Precio de oferta</Text>
          </View>

          {!items ? (
             <View style={styles.row}>
               <Text style={styles.cellTextProduct}>No hay productos</Text>
             </View>
          ) : (
            items.map((item, index) => {
              const isEven = index % 2 !== 0; // Для чередования цвета
              return (
                <View wrap={false} key={index} style={[styles.row, isEven && styles.rowEven]}>
                  <View style={styles.colSku}>
                    <Text style={styles.cellTextSku}>{item?.sku || 'N/A'}</Text>
                  </View>
                  <View style={styles.colProduct}>
                    <Text style={styles.cellTextProduct}>{item?.name || 'Producto sin nombre'}</Text>
                  </View>
                  <View style={styles.colPriceCat}>
                    <Text style={styles.cellTextPriceCat}>
                      {formatPrice(item?.marketPriceCP, item?.IVACP)}
                    </Text>
                  </View>
                  <View style={styles.colPriceComp}>
                    <Text style={styles.cellTextPriceComp}>
                      {formatPrice(item?.spoPrice || item?.marketPriceCP, item?.IVACP)}
                    </Text>
                  </View>
                </View>
              )
            })
          )}
        </View>

        {/* Футер */}
        <View fixed style={styles.footer}>
          <View style={styles.footerItem}>
            <Svg viewBox="0 0 24 24" width="14" height="14">
              <Path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.footerText}>+34 673 743 464</Text>
          </View>
          <View style={styles.footerItem}>
            <Svg viewBox="0 0 24 24" width="14" height="14">
              <Path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.footerText}>+34 673 743 464</Text>
          </View>
          {/* <View style={styles.footerItem}>
            <Svg viewBox="0 0 24 24" width="14" height="14">
              <Path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M22 6l-10 7L2 6" fill="none" stroke={COLORS.primaryGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.footerText}>info@tuproductoonline.es</Text>
          </View> */}
        </View>

      </Page>
    </Document>
  );
};

export default PDFOrders;